import React, { useCallback, useEffect, useRef, useState } from "react"
import { Modal, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faDatabase,
  faFileExport,
  faFloppyDisk,
  faPlay,
  faPlus,
  faSliders,
  faTableColumns,
  faTag,
} from "@fortawesome/free-solid-svg-icons"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "api/queryKeys"
import { useUserStore } from "stores/userStore"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import MenuButton, { MenuButtonItem } from "shared/ui/MenuButton/MenuButton.component"
import {
  LoadedSpec,
  TabSpecConflictError,
  useTabSpecMutations,
} from "../../api/useTabSpecFiles"
import {
  selectIsDirty,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../../runtime/tabSpecStore"
import type { Approval, TabSpecDocument } from "../../types/tabSpecDocument"
import { runAvailability } from "../Run/runAvailability"

/**
 * The z-index of the spec's own dialogs: the save conflict, the overwrite
 * question and the leave guard. The spec's drawers and dialogs sit at antd's
 * 1000, where the one added to the page last paints on top, and a dialog
 * stays in the page after its first opening: without this, a conflict could
 * open hidden under a drawer opened since (the banner editor, say), with the
 * keyboard focus inside it.
 */
export const SPEC_DIALOG_Z_INDEX = 1001

const APPROVAL_TEXT: Record<Approval["state"], string> = {
  draft: "Draft",
  "in-review": "In review",
  approved: "Approved",
}

export interface SpecHeaderProps {
  onOpenDefaults: () => void
  onOpenData: () => void
  /** Opens the stored version in place of the draft; the conflict dialog offers it. */
  onReload?: () => void | Promise<void>
  /** Opens the "Add a table" dialog; without it the button is disabled. */
  onAddTable?: () => void
  /** Opens the banner editor; without it the button is disabled. */
  onOpenBanners?: () => void
  /** Opens the Labels drawer (the names the tables show); without it the button is disabled. */
  onOpenLabels?: () => void
  /**
   * Opens the run dialog; without it the button is disabled. It is also
   * disabled while the spec cannot run, with the reason as its tooltip.
   */
  onRunBook?: () => void
  /**
   * Opens the download dialog; without it the button is disabled. Tables
   * not run do not disable it: the dialog says how many there are.
   */
  onDownload?: () => void
  /** Shown after the approval, as the dummy data badge the workspace passes. */
  badge?: React.ReactNode
  /** Extra actions at the right end of the row (the assistant's toggle). */
  actions?: React.ReactNode
}

/**
 * The spec's top row, one line that never wraps: the name (truncated, with
 * a tooltip) and its approval over a saved-state hairline (accent while
 * there are unsaved changes), then on the right "Settings ▾" (the Banners,
 * Defaults, Data and Labels panels), Add table, Download Excel, Save and
 * Run tab book, the one solid button, then `actions` (the assistant's
 * toggle). Save (also Ctrl+S) writes the draft over the version it was
 * loaded from; a file someone else saved since is
 * never overwritten silently: the conflict dialog offers their version (the
 * draft is dropped), saving the draft over theirs after a second question,
 * or more editing.
 */
const SpecHeader: React.FC<SpecHeaderProps> = ({
  onOpenDefaults,
  onOpenData,
  onReload,
  onAddTable,
  onOpenBanners,
  onOpenLabels,
  onRunBook,
  onDownload,
  badge,
  actions,
}) => {
  const store = useTabSpecStoreApi()
  const name = useTabSpecStore((s) => s.name)
  const projectId = useTabSpecStore((s) => s.projectId)
  const owner = useTabSpecStore((s) => s.owner)
  const approval = useTabSpecStore((s) => s.document.approval.state)
  const dirty = useTabSpecStore(selectIsDirty)
  // The reason alone, a string or null: a new object on every call would
  // re-render without end.
  const runBlocked = useTabSpecStore((s) => runAvailability(s).reason)
  const getUserFullName = useUserStore((s) => s.getUserFullName)
  const client = useQueryClient()
  const { save } = useTabSpecMutations(projectId, owner)
  const [saving, setSaving] = useState(false)
  /** The conflict dialog, then the question before the draft overwrites their version. */
  const [conflict, setConflict] = useState<"conflict" | "overwrite" | null>(null)
  const busy = useRef(false)
  const rowRef = useRef<HTMLDivElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const dialogContainer = (): HTMLElement =>
    rowRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  /**
   * Saves the draft. `overwrite` writes it whatever the stored file holds
   * now (the save without an etag); otherwise a file changed since it was
   * loaded is refused and the conflict dialog opens.
   */
  const write = useCallback(async (overwrite: boolean): Promise<void> => {
    // The text fields write what is typed when they are left: leave the one
    // the cursor is in (Ctrl+S keeps the focus), so the save holds its text,
    // then go back to it so the user keeps their place.
    const active = document.activeElement
    const field =
      active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
        ? active
        : null
    field?.blur()
    const state = store.getState()
    if (field?.isConnected) field.focus()
    if (busy.current || !selectIsDirty(state)) return
    busy.current = true
    setSaving(true)
    const sent: TabSpecDocument = {
      ...state.document,
      updatedBy: getUserFullName(),
    }
    try {
      const { etag } = await save(state.name, sent, overwrite ? null : state.loadedEtag)
      // The save stamps the time and puts the stored document in the cache;
      // the snapshot is that document, so the draft is clean only when
      // nothing changed during the save.
      const stored = client.getQueryData<LoadedSpec | null>(
        queryKeys.tabSpecs.detail(state.projectId, state.owner, state.name.trim())
      )
      const saved =
        stored?.etag === etag && stored.result.ok ? stored.result.document : sent
      store.getState().markSaved(saved, etag)
      notify.success("Tab spec saved")
    } catch (e) {
      if (e instanceof TabSpecConflictError) setConflict("conflict")
      else
        notify.error(
          "Tab spec",
          e instanceof Error && e.message ? e.message : "The tab spec could not be saved."
        )
    } finally {
      busy.current = false
      setSaving(false)
    }
  }, [store, save, client, getUserFullName])
  const onSave = useCallback((): Promise<void> => write(false), [write])

  const saveRef = useRef(onSave)
  useEffect(() => {
    saveRef.current = onSave
  }, [onSave])
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault()
        void saveRef.current()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  // The spec's panels, one menu so the row stays on one line. Banners and
  // Labels stay off until the workspace gives them a handler.
  const settingsItems: MenuButtonItem[] = [
    {
      key: "banners",
      label: "Banners",
      icon: <FontAwesomeIcon icon={faTableColumns} fixedWidth />,
      disabled: !onOpenBanners,
      onClick: onOpenBanners,
    },
    {
      key: "defaults",
      label: "Defaults for every table",
      icon: <FontAwesomeIcon icon={faSliders} fixedWidth />,
      onClick: onOpenDefaults,
    },
    {
      key: "data",
      label: "Data",
      icon: <FontAwesomeIcon icon={faDatabase} fixedWidth />,
      onClick: onOpenData,
    },
    {
      key: "labels",
      label: "Labels",
      icon: <FontAwesomeIcon icon={faTag} fixedWidth />,
      disabled: !onOpenLabels,
      onClick: onOpenLabels,
    },
  ]

  return (
    <div
      ref={rowRef}
      className="ts-header flex-nowrap"
      role="toolbar"
      aria-label="Tab spec actions"
    >
      <div className="ts-header__title">
        <div className="ts-header__row">
          <Tooltip title={name} placement="bottomLeft">
            <h2 className="ts-header__name">{name}</h2>
          </Tooltip>
          <span className="ts-chip ts-header__approval">{APPROVAL_TEXT[approval]}</span>
          {badge}
        </div>
        <div
          className="ts-saved-bar"
          data-dirty={dirty}
          title={dirty ? "Unsaved changes" : "Saved"}
        />
        <span className="sr-only" aria-live="polite">
          {dirty ? "Unsaved changes" : ""}
        </span>
      </div>
      <div className="ts-header__actions flex-nowrap">
        <MenuButton label="Settings" items={settingsItems} />
        <DataQButton type="secondary" isDisabled={!onAddTable} onClick={onAddTable}>
          <FontAwesomeIcon icon={faPlus} />
          Add table
        </DataQButton>
        <DataQButton type="secondary" isDisabled={!onDownload} onClick={onDownload}>
          <FontAwesomeIcon icon={faFileExport} />
          Download Excel
        </DataQButton>
        <Tooltip title={`${dirty ? "Unsaved changes" : "Saved"} (Ctrl+S)`}>
          {/* A disabled button takes no pointer events; the span holds the tooltip. */}
          <span className="inline-block">
            <DataQButton
              type="secondary"
              isDisabled={saving || !dirty}
              onClick={() => void onSave()}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
              {saving ? "Saving" : "Save"}
            </DataQButton>
          </span>
        </Tooltip>
        <Tooltip title={runBlocked}>
          <span className="inline-block">
            <DataQButton
              type="primary"
              isDisabled={!onRunBook || runBlocked !== null}
              onClick={onRunBook}
            >
              Run tab book
              <FontAwesomeIcon icon={faPlay} />
            </DataQButton>
          </span>
        </Tooltip>
        {actions}
      </div>
      {/* The spec's own confirmations keep their place over the drawers
          (SPEC_DIALOG_Z_INDEX); their footers are the platform's buttons:
          the way back first, the confirming action last. */}
      <Modal
        open={conflict === "conflict"}
        title="This tab spec was changed by someone else since you opened it."
        onCancel={() => setConflict(null)}
        getContainer={dialogContainer}
        zIndex={SPEC_DIALOG_Z_INDEX}
        footer={[
          ...(onReload
            ? [
                <DataQButton
                  key="reload"
                  type="secondary"
                  onClick={() => {
                    setConflict(null)
                    void onReload()
                  }}
                >
                  Reload their version
                </DataQButton>,
              ]
            : []),
          <DataQButton key="overwrite" type="danger" onClick={() => setConflict("overwrite")}>
            Save mine over theirs
          </DataQButton>,
          <DataQButton key="keep" type="primary" onClick={() => setConflict(null)}>
            Keep editing
          </DataQButton>,
        ]}
      >
        <p>
          Reloading their version discards the changes you have not saved. If
          you were the last to save, reload to continue.
        </p>
      </Modal>
      <Modal
        open={conflict === "overwrite"}
        title="Save mine over theirs"
        onCancel={() => setConflict(null)}
        getContainer={dialogContainer}
        zIndex={SPEC_DIALOG_Z_INDEX}
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={() => setConflict(null)}>
            Cancel
          </DataQButton>,
          <DataQButton
            key="save"
            type="danger"
            onClick={() => {
              setConflict(null)
              void write(true)
            }}
          >
            Save mine
          </DataQButton>,
        ]}
      >
        <p>Their changes will be lost. Save your version?</p>
      </Modal>
    </div>
  )
}

export default SpecHeader
